import {Add} from "@mui/icons-material";
import {Card, Divider, Stack, Typography} from "@mui/material";
import React from "react";
import {cat, cos, Expense, formatCurrency, getExpenseCatTextFromSN} from "../../../firebase/index.js";
import {APXHideOnScrollFab} from "../../../reusables/APXHideOnScrollFab.jsx";
import type {SortOption} from "../../../reusables/APXSortOptions.jsx";
import APXSortOptions, {SortOptions} from "../../../reusables/APXSortOptions.jsx";
import {singleLineText} from "../../../reusables/js/APXCrucialStyles.js";
import {expenseColour, white} from "../../../reusables/js/APXTheme.js";
import {nonPlusLimits, useInMemoryPlusStatus} from "../../../reusables/plus/APXPlus.js";
import {APXPlusRequiredAlert} from "../../../reusables/plus/APXPlusAlerts.jsx";
import AddNewExpenseBSL from "./AddNewExpenseBSL.jsx";
import ExpenseCard from "./ExpenseCard.jsx";


const sortOptions: SortOptions = {
	  CostLowToHigh: {
			 label: `${cos}: Low to High`,
			 compareFunction: (a: Expense, b: Expense): number => a.expenseCost - b.expenseCost
	  },
	  CostHighToLow: {
			 label: `${cos}: High to Low`,
			 compareFunction: (a: Expense, b: Expense): number => b.expenseCost - a.expenseCost
	  },
	  DateAsc: {
			 label: "Date: Oldest to Latest",
			 compareFunction: (a: Expense, b: Expense): number => new Date(a.expenseMillis).getTime() - new Date(b.expenseMillis).getTime()
	  },
	  DateDesc: {
			 label: "Date: Latest to Oldest",
			 compareFunction: (a: Expense, b: Expense): number => new Date(b.expenseMillis).getTime() - new Date(a.expenseMillis).getTime()
	  },
	  CategoryAsc: {
			 label: `${cat}: Ascending`,
			 compareFunction: (a: Expense, b: Expense): number => getExpenseCatTextFromSN(a.expenseSerialNumber).localeCompare(getExpenseCatTextFromSN(b.expenseSerialNumber))
	  },
	  CategoryDesc: {
			 label: `${cat}: Descending`,
			 compareFunction: (a: Expense, b: Expense): number => getExpenseCatTextFromSN(b.expenseSerialNumber).localeCompare(getExpenseCatTextFromSN(a.expenseSerialNumber))
	  }
};

export default function ExpensesLayout({uid, industryKey, exps}: { uid: string, industryKey: string, exps: Expense[] }) {
	  
	  const expenses: Expense[] = exps;
	  const [sortedExpenses, setSortedExpenses] = React.useState([...expenses].reverse());
	  const [total, setTotal] = React.useState(0);
	  React.useEffect(() => {
			 setSortedExpenses([...expenses].reverse());
			 setTotal(expenses.reduce((previousValue, currentValue) => previousValue + currentValue.expenseCost, 0));
	  }, [expenses]);
	  const [openNewExpenseBSL, setOpenNewExpenseBSL] = React.useState(false);
	  
	  const {isPlusUser} = useInMemoryPlusStatus();
	  
	  return (
				 <>
						<Stack spacing={2} p={0} alignItems={"center"} justifyContent={"center"}>
							  <Stack p={2} sx={{width: "100%"}} alignItems={"stretch"} justifyContent={"center"}>
									 <Card variant={"outlined"} sx={{borderColor: expenseColour}}>
											<Stack p={2} sx={{backgroundColor: expenseColour}}>
												  <Typography mr={1} variant={"h4"} align={"right"} sx={{textShadow: "0px 1px 1px rgba(0, 0, 0, 0.25)"}} style={singleLineText}
																  fontFamily={"Monospace"}>{formatCurrency(total)}</Typography>
											</Stack>
											<Stack p={2} my={2}>
												  <APXSortOptions sortOptions={sortOptions} onSelect={(sortOption: SortOption) => {
														 setSortedExpenses((prevState) => [...prevState].sort(sortOption.compareFunction));
												  }}/>
											</Stack>
											<Stack p={2}>
												  {sortedExpenses &&
															 sortedExpenses.map((e, index, array) =>
																		<React.Fragment key={e.expenseMillis}>
																			  <ExpenseCard uid={uid} industryKey={industryKey} e={e}/>
																			  {index < array.length - 1 && <Divider style={{margin: 2, backgroundColor: expenseColour, marginLeft: 16, marginRight: 16}}/>}
																		</React.Fragment>
															 )
												  }
											</Stack>
									 </Card>
							  </Stack>
						</Stack>
						<APXHideOnScrollFab icon={<Add/>} backgroundColour={expenseColour} colour={white}
												  sx={{position: "fixed", bottom: 16, right: 16}}
												  onClick={() => setOpenNewExpenseBSL(true)}
						/>
						{openNewExpenseBSL ?
								  (
											 (isPlusUser)
											 ||
											 (/*NOT->*/ !isPlusUser && /*TRUTHY->*/ !!sortedExpenses && sortedExpenses.length < nonPlusLimits.exp.numerical)
								  ) ?
											 <AddNewExpenseBSL exp={null} uid={uid} industryKey={industryKey} onClose={() => setOpenNewExpenseBSL(false)}/>
											 :
											 <APXPlusRequiredAlert/>
								  :
								  null
						}
				 </>
	  );
}
